import { OnboardingTarget } from "../onboarding/onboardingTargets";
import AddIcon from "@mui/icons-material/Add";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { CircularProgress } from "@mui/material";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import classNames from "classnames";
import {
  KeyboardEvent,
  MouseEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  selectFilteredTakes,
  selectSelectedTakeId,
  setSelectedTake,
  updateSelectedTake,
} from "../../app/slices/TrainingSlice";
import PiConfirmationDialog from "../../components/PiConfirmationDialog";
import PiPopoverMenu from "../../components/PiPopoverMenu";
import { AssetDetailsDTO, AssetKey } from "../../dto/AssetDTO";
import { useSmallAssetPreview } from "../../hooks/SmallAssetPreviewHook";
import Take360 from "../../models/Take360";
import EditorColumn, {
  EditorColumnFooterButton,
  EditorColumnHeader,
  EditorColumnRow,
  EditorColumnEmptyState,
} from "../editor_columns/EditorColumn";
import FileExplorerDialog from "../file_explorer/FileExplorerDialog";
import PiMenuButton from "../menu/PiMenuButton";
import { useAddNewBubble } from "./SelectNewBubbleHook";
import {
  getTakeDndId,
  TakeInteractionDndData,
  useTakeInteractionDndContext,
} from "./TakeInteractionDndProvider";
import useDeleteBubble from "./DeleteBubble.hook";

export function Take360ColumnRowView(props: {
  take: Take360;
  selected?: boolean;
  dropTarget?: boolean;
  ghost?: boolean;
  onClick?: () => void;
  onRename?: (name: string) => void;
  onDelete?: () => void;
  dragAttributes?: any;
  dragListeners?: any;
}) {
  const {
    take,
    selected,
    dropTarget,
    ghost,
    onClick,
    onRename,
    onDelete,
    dragAttributes,
    dragListeners,
  } = props;
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [isRenaming, setIsRenaming] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { getAssetPreview } = useSmallAssetPreview(take.assetId);

  useEffect(() => {
    if (isRenaming) inputRef.current?.focus();
  }, [isRenaming]);

  useEffect(() => {
    setIsRenaming(false);
  }, [selected]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key !== "Enter" && event.key !== " ") return;

    event.preventDefault();
    onClick?.();
  };

  const stopRowAction = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  const startRename = () => {
    onClick?.();
    setIsRenaming(true);
  };

  const thumbnail = (
    <div className="relative w-[44px] h-[44px] rounded-5 overflow-hidden bg-[var(--pi-timeline-interaction-bg)] border border-creator-border flex-shrink-0">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center text-creator-primary">
          <CircularProgress size={18} color="inherit" />
        </div>
      )}
      <img
        src={getAssetPreview()}
        alt=""
        className={classNames(
          "block w-full h-full object-cover",
          isLoading && "opacity-0"
        )}
        onLoad={() => setIsLoading(false)}
      />
    </div>
  );

  const title = isRenaming ? (
    <input
      ref={inputRef}
      className="w-full min-w-0 rounded-5 border border-creator-border bg-creator-surface px-1 text-[13px] font-normal leading-5 text-creator-text outline-none"
      value={take.name}
      onBlur={() => setIsRenaming(false)}
      onClick={stopRowAction}
      onContextMenu={stopRowAction}
      onChange={(event) => onRename?.(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === "NumpadEnter") {
          setIsRenaming(false);
        }
      }}
    />
  ) : (
    take.name
  );

  const menu = !ghost && (
    <span
      className="my-[-4px] flex-shrink-0"
      onClick={stopRowAction}
      onContextMenu={stopRowAction}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <PiPopoverMenu softIconHover modernMenu>
        <PiMenuButton
          text={t("rename")}
          icon={<EditOutlinedIcon color="inherit" fontSize="inherit" />}
          onClick={startRename}
          variant="modern"
        />
        <PiMenuButton
          text={t("delete")}
          icon={<DeleteOutlineOutlinedIcon color="inherit" fontSize="inherit" />}
          onClick={onDelete}
          variant="modern"
          tone="danger"
        />
      </PiPopoverMenu>
    </span>
  );

  return (
    <EditorColumnRow
      leading={
        <span className="flex items-center justify-center gap-2 text-creator-text-sub flex-shrink-0">
          <DragIndicatorIcon sx={{ fontSize: 14 }} />
          {thumbnail}
        </span>
      }
      title={title}
      titleClassName="font-normal"
      subtitle={take.is360Image === false ? "2D" : "360"}
      trailing={menu}
      selected={selected}
      dropTarget={dropTarget}
      ghost={ghost}
      defaultBorder
      onClick={onClick}
      onKeyDown={handleKeyDown}
      dragAttributes={dragAttributes}
      dragListeners={dragListeners}
      testId="take360-column-row"
      className="min-h-[58px]"
    />
  );
}

function SortableTake360ColumnRow(props: {
  take: Take360;
  selected?: boolean;
  dropTarget?: boolean;
}) {
  const { take } = props;
  const dispatch = useDispatch();
  const { deleteBubble } = useDeleteBubble();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: getTakeDndId(take.id),
      data: { kind: "take", takeId: take.id } as TakeInteractionDndData,
      transition: {
        duration: 150,
        easing: "cubic-bezier(0.25, 1, 0.5, 1)",
      },
    });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Take360ColumnRowView
        {...props}
        onClick={() => dispatch(setSelectedTake(take.id))}
        onRename={(name) => dispatch(updateSelectedTake({ ...take, name }))}
        onDelete={() => setIsDeleteDialogOpen(true)}
        dragAttributes={attributes}
        dragListeners={listeners}
      />
      <PiConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={() => deleteBubble(take)}
        text={t("delete_bubble.delete_description")}
        title={t("delete_bubble.delete_title")}
        confirmText={t("delete_bubble.confirm_delete")}
        cancelText={t("delete_bubble.cancel_delete")}
        height="215px"
        confirmButtonVariant="piRed"
      />
    </div>
  );
}

export default function Take360Column() {
  const { t } = useTranslation();
  const takes = useSelector(selectFilteredTakes);
  const selectedTakeId = useSelector(selectSelectedTakeId);
  const takeRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const { hoverTakeId } = useTakeInteractionDndContext();
  const [addBubbles, isDialogOpen, setIsDialogOpen] = useAddNewBubble();

  useEffect(() => {
    if (!selectedTakeId) return;
    takeRefs.current[selectedTakeId]?.scrollIntoView?.({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }, [selectedTakeId]);

  const onAddBubble = async (assets: AssetDetailsDTO[]) => {
    await addBubbles(assets);
    setTimeout(() => {
      if (scrollerRef.current) {
        scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
      }
    }, 100);
  };

  return (
    <EditorColumn
      testId="take360-column"
      onboardingId={OnboardingTarget.Editor360TakesColumn}
      header={
        <EditorColumnHeader
          icon={<DashboardOutlinedIcon fontSize="inherit" />}
          title={t("bubbles")}
          testId="take360-column-header"
        />
      }
      footer={
        <EditorColumnFooterButton
          label={t("add_bubble")}
          icon={<AddIcon fontSize="inherit" />}
          onClick={() => setIsDialogOpen(true)}
          id="add-bubble-btn"
          testId="take360-add-bubble"
          onboardingId={OnboardingTarget.Editor360AddTake}
        />
      }
    >
      <div ref={scrollerRef} className="space-y-1.5">
        {takes.length > 0 ? (
          <SortableContext
            items={takes.map((take) => getTakeDndId(take.id))}
            strategy={verticalListSortingStrategy}
            id="takes360"
          >
            {takes.map((take) => (
              <div
                key={take.id}
                ref={(ref) => (takeRefs.current[take.id] = ref)}
              >
                <SortableTake360ColumnRow
                  take={take}
                  selected={take.id === selectedTakeId}
                  dropTarget={take.id === hoverTakeId}
                />
              </div>
            ))}
          </SortableContext>
        ) : (
          <EditorColumnEmptyState icon={<ImageOutlinedIcon />}>
            {t("onboarding.empty.no_takes")}
          </EditorColumnEmptyState>
        )}
      </div>
      <FileExplorerDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        type={AssetKey.Image}
        onSelect={(asset) => onAddBubble([asset])}
        allowMultiSelect
        onMultiSelect={onAddBubble}
      />
    </EditorColumn>
  );
}
